import React from "react";
import { Link, withRouter } from "react-router-dom";
import useDocumentMeta from "./useDocumentMeta";

const SERVICES = [
    {
        title: 'Web applications',
        text: 'Dashboards, portals, and SaaS products built with React, Next.js, and Node.js.',
        icon: <><rect x="3" y="4" width="18" height="14" rx="2" /><path d="M3 9h18M8 21h8" /></>,
    },
    {
        title: 'iOS & Android apps',
        text: 'Cross-platform mobile apps, from first prototype through App Store and Google Play.',
        icon: <><rect x="7" y="2" width="10" height="20" rx="2" /><path d="M11 18h2" /></>,
    },
    {
        title: 'AI features',
        text: 'Practical LLM integrations: assistants, content generation, image recognition, and workflow automation.',
        icon: <><path d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z" /><path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" /></>,
    },
    {
        title: 'Add-ons & integrations',
        text: 'Adobe Express and Canva add-ons, third-party APIs, and connections between the tools people already use.',
        icon: <><path d="M9 3h6v4a2 2 0 1 0 4 0h2v6h-2a2 2 0 1 0 0 4h2v4H3V3h6" /></>,
    },
    {
        title: 'Commerce & inventory',
        text: 'Point-of-sale flows, pricing engines, inventory tracking, and payment processing.',
        icon: <><path d="M3 4h2l2.4 11.2a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.5L21 8H6" /><circle cx="10" cy="20" r="1" /><circle cx="17" cy="20" r="1" /></>,
    },
    {
        title: 'Cloud & AWS',
        text: 'Serverless backends, APIs, and infrastructure on AWS that stays fast and affordable as products grow.',
        icon: <><path d="M18 10h-1.3A7 7 0 1 0 9 19h9a4.5 4.5 0 0 0 0-9z" /></>,
    },
];



function ArrowIcon() {
    return (
        <svg className="icon-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
    );
}


function HomePageLayout() {
    useDocumentMeta({
        title: 'Yay Fun, Inc. | Software Product Studio in Austin, TX',
        description: 'Yay Fun, Inc. is a software product studio in Austin, Texas, and the maker of PokeSwift and Social Bellow.',
        path: '/',
    });


    return (
        <>
            {/* Hero */}
            <section className="hero">
                <div className="container hero__inner">
                    <div className="hero__copy">
                        <p className="eyebrow reveal" style={{ '--d': '0ms' }}>
                            <span className="eyebrow__dot" aria-hidden="true"></span>
                            Software product studio · Austin, TX
                        </p>
                        <h1 className="hero__title reveal" style={{ '--d': '80ms' }}>
                            We build software products.
                        </h1>
                        <p className="hero__lede reveal" style={{ '--d': '160ms' }}>
                            Yay Fun is a software studio in Austin, Texas. We design, build, and run our own
                            apps across web, mobile, and AI, including PokeSwift and Social Bellow.
                        </p>
                        <div className="hero__actions reveal" style={{ '--d': '240ms' }}>
                            <Link to="/#products" className="btn btn--primary btn--lg">
                                See our products <ArrowIcon />
                            </Link>
                            <Link to="/about" className="btn btn--ghost btn--lg">
                                About us
                            </Link>
                        </div>
                        <ul className="hero__proof reveal" style={{ '--d': '320ms' }} aria-label="Where our products are available">
                            <li>App Store</li>
                            <li>Google Play</li>
                            <li>Adobe Express</li>
                            <li>Canva</li>
                        </ul>
                    </div>

                    <div className="hero__visual reveal" style={{ '--d': '200ms' }} aria-hidden="true">
                        <div className="shot shot--main">
                            <div className="shot__bar"><i></i><i></i><i></i><span>pokeswift.com</span></div>
                            <img src="/images/apps/pokeswift-hero-1400.png" alt="" width="1081" height="757" />
                        </div>
                        <div className="shot shot--float">
                            <img src="/images/apps/socialbellow-adobe-express-1100.jpg" alt="" width="1100" height="647" />
                        </div>
                        <div className="hero__chip">
                            <img src="/images/apps/social-bellow-icon-112.png" alt="" width="28" height="28" />
                            <span>Featured on Adobe Express</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Products */}
            <section className="section" id="products" aria-labelledby="products-title">
                <div className="container">
                    <div className="section-head">
                        <p className="kicker">Our products</p>
                        <h2 className="section-title" id="products-title">What we’re building</h2>
                        <p className="section-lede">
                            Every product is designed, built, and run in-house, from the first prototype
                            to the app store and beyond.
                        </p>
                    </div>

                    <article className="product">
                        <div className="product__body">
                            <div className="product__heading">
                                <img src="/images/apps/pokeswift-icon-128.png" alt="PokeSwift app icon" width="56" height="56" className="product__icon" loading="lazy" />
                                <div>
                                    <p className="product__tag">iOS & Android · Point of sale</p>
                                    <h3 className="product__title">PokeSwift</h3>
                                </div>
                            </div>
                            <p className="product__desc">
                                A fast checkout app for Pokémon card vendors. PokeSwift pulls live market
                                pricing, recognizes cards with AI, and tallies sales in seconds, so vendors
                                can keep the line moving at trade shows.
                            </p>
                            <div className="product__story">
                                <div className="product__story-icon" aria-hidden="true">
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M3 9l1.5-5h15L21 9" /><path d="M3 9h18v1.5a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0z" /><path d="M5 13.5V20h14v-6.5" /><path d="M10 20v-4h4v4" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="product__story-title">Built at our own booth</h4>
                                    <p className="product__story-text">
                                        We also run a Pokémon card business and vend at card shows and conventions.
                                        PokeSwift started as the tool we needed at our own table, and we use it at our
                                        shows to price cards, ring up sales, and keep the line moving. Every feature
                                        gets tested on a real show floor before it ships.
                                    </p>
                                </div>
                            </div>
                            <ul className="checklist">
                                <li>Instant comps from real-time market data</li>
                                <li>AI-powered card recognition</li>
                                <li>Quick, accurate checkout at busy shows</li>
                            </ul>
                            <div className="product__actions">
                                <a href="https://apps.apple.com/us/app/pokeswift/id6759176184" target="_blank" rel="noopener noreferrer" className="store-badge">
                                    <img src="/images/ios-download.png.webp" alt="Download PokeSwift on the App Store" loading="lazy" />
                                </a>
                                <a href="https://play.google.com/store/apps/details?id=com.pokeswift.app" target="_blank" rel="noopener noreferrer" className="store-badge">
                                    <img src="/images/android-download.png.webp" alt="Get PokeSwift on Google Play" loading="lazy" />
                                </a>
                            </div>
                            <a href="https://pokeswift.com" target="_blank" rel="noopener noreferrer" className="text-link">
                                Visit pokeswift.com <ArrowIcon />
                            </a>
                        </div>
                        <div className="product__media product__media--dark">
                            <img src="/images/apps/pokeswift-hero-1400.png" alt="PokeSwift website showing the point-of-sale app for trading card shows" width="1081" height="757" loading="lazy" />
                        </div>
                    </article>

                    <article className="product product--reverse">
                        <div className="product__body">
                            <div className="product__heading">
                                <img src="/images/apps/social-bellow-icon-112.png" alt="Social Bellow app icon" width="56" height="56" className="product__icon" loading="lazy" />
                                <div>
                                    <p className="product__tag">Adobe Express & Canva add-on · AI</p>
                                    <h3 className="product__title">Social Bellow</h3>
                                </div>
                            </div>
                            <p className="product__desc">
                                An AI content companion that lives inside the design tools creators already use.
                                Social Bellow writes captions and copy, discusses designs, and generates images
                                without leaving Adobe Express or Canva.
                            </p>
                            <ul className="checklist">
                                <li>Featured in the Adobe Express add-on marketplace</li>
                                <li>Custom prompts and AI image generation</li>
                                <li>Built for creators, marketers, and small teams</li>
                            </ul>
                            <div className="product__actions">
                                <a href="https://new.express.adobe.com/new?category=addOns&launchAddOnId=wh35k4i3l" target="_blank" rel="noopener noreferrer" className="pill-link">
                                    Open in Adobe Express
                                </a>
                                <a href="https://www.canva.com/your-apps/AAFjMZ7f2uQ/social-bellow" target="_blank" rel="noopener noreferrer" className="pill-link">
                                    Open in Canva
                                </a>
                            </div>
                            <a href="https://socialbellow.com" target="_blank" rel="noopener noreferrer" className="text-link">
                                Visit socialbellow.com <ArrowIcon />
                            </a>
                        </div>
                        <div className="product__media product__media--violet">
                            <img src="/images/apps/socialbellow-adobe-express-1100.jpg" alt="Social Bellow add-on generating content inside Adobe Express" width="1100" height="647" loading="lazy" />
                        </div>
                    </article>

                    <figure className="featured">
                        <img src="/images/apps/adobe-express-featured-add-ons.jpg" alt="Social Bellow listed among featured add-ons in Adobe Express alongside TikTok, Google Drive, and WellSaid Labs" width="1024" height="250" loading="lazy" />
                        <figcaption>Social Bellow in Adobe Express’s featured add-ons, alongside TikTok, WellSaid Labs, and Google Drive.</figcaption>
                    </figure>
                </div>
            </section>

            {/* What we do */}
            <section className="section section--tint" id="what-we-do" aria-labelledby="what-we-do-title">
                <div className="container">
                    <div className="section-head section-head--split">
                        <div>
                            <p className="kicker">What we do</p>
                            <h2 className="section-title" id="what-we-do-title">The work behind our products.</h2>
                        </div>
                        <p className="section-lede">
                            Our team handles design, engineering, and infrastructure for everything we ship.
                        </p>
                    </div>

                    <div className="services">
                        {SERVICES.map(service => (
                            <div className="service" key={service.title}>
                                <div className="service__icon">
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        {service.icon}
                                    </svg>
                                </div>
                                <h3 className="service__title">{service.title}</h3>
                                <p className="service__text">{service.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>



            {/* Studio facts */}
            <section className="facts" aria-labelledby="facts-title">
                <div className="container facts__inner">
                    <div>
                        <h2 className="facts__title" id="facts-title">Made in Austin, Texas.</h2>
                        <p className="facts__text">A small team building and running its own software.</p>
                    </div>
                    <dl className="facts__list">
                        <div>
                            <dt>Building software since</dt>
                            <dd>2012</dd>
                        </div>
                        <div>
                            <dt>Products in the market</dt>
                            <dd>2</dd>
                        </div>
                        <div>
                            <dt>Platforms we ship on</dt>
                            <dd>4</dd>
                        </div>
                    </dl>
                </div>
            </section>
        </>
    )
}

export default withRouter(HomePageLayout)
